import { ReactNode } from 'react'
import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

export interface Moment {
  time: string
  title: string
  body: string
}

interface PersonaRoutineProps {
  eyebrow?: string
  title: ReactNode
  body?: string
  moments: Moment[]
}

export default function PersonaRoutine({
  eyebrow = 'A day with Monofor',
  title,
  body,
  moments,
}: PersonaRoutineProps) {
  return (
    <Section tone="subtle" density="default">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow>{eyebrow}</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            {title}
          </Heading>
          {body && (
            <Text size="xl" tone="secondary" className="mt-6">
              {body}
            </Text>
          )}
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {moments.map((m, i) => (
            <motion.div
              key={m.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
              className="rounded-3xl border border-line-subtle bg-surface p-8 lg:p-10 shadow-card"
            >
              <div className="text-eyebrow font-semibold uppercase text-accent-700">
                {m.time}
              </div>
              <h3 className="mt-4 text-display-sm font-medium text-ink-primary">
                {m.title}
              </h3>
              <Text size="md" tone="secondary" className="mt-3">
                {m.body}
              </Text>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  )
}
